import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiResponse, useMemoizedArrayData } from 'hooks/api/api';
import {
  AutomationsWorkflowsService,
  AutomationsWorkflowsTriggersService,
  WorkflowListResponse,
  WorkflowMetadata,
} from 'src/api/generated';
import type {
  TCWorkflowCreateBodyParams,
  TCWorkflowCreateResponse,
  TCWorkflowCreateTriggerBodyParams,
} from 'src/api/generated';

interface UseWorkflowsResponse extends ApiResponse {
  workflows: WorkflowMetadata[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const useAutomationsWorkflows = (
  limit?: number,
  enabled = true
): UseWorkflowsResponse => {
  const fetchWorkflows = async ({
    pageParam,
  }: {
    pageParam: unknown;
  }): Promise<WorkflowListResponse> => {
    return AutomationsWorkflowsService.listWorkflows(
      limit,
      pageParam as string | undefined
    );
  };

  const getNextPageParam = (lastPage: WorkflowListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<WorkflowListResponse, Error>({
    queryKey: ['automations/workflows', { limit }],
    queryFn: fetchWorkflows,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const workflows = useMemoizedArrayData<WorkflowMetadata>(
    data?.pages.flatMap((p) => p.items) || []
  );

  return {
    loading,
    error: error || null,
    workflows,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

export const useCreateWorkflowMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    TCWorkflowCreateResponse,
    Error,
    TCWorkflowCreateBodyParams
  >({
    mutationFn: async (body) =>
      AutomationsWorkflowsService.createWorkflow(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['automations/workflows'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['automations/tenant-automations'],
      });
    },
  });
};

export const useCreateWorkflowTriggerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      workflowId,
      body,
    }: {
      workflowId: string;
      body: TCWorkflowCreateTriggerBodyParams;
    }) => {
      return AutomationsWorkflowsTriggersService.createWorkflowTrigger(
        workflowId,
        body
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['automations/workflows-triggers'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['automations/tenant-automations'],
      });
    },
  });
};

export const useDeleteWorkflowTriggerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      workflowId,
      version,
      triggerId,
    }: {
      workflowId: string;
      version: string;
      triggerId: string;
    }) => {
      return AutomationsWorkflowsTriggersService.deleteWorkflowTrigger(
        workflowId,
        version,
        triggerId
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['automations/workflows-triggers'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['automations/tenant-automations'],
      });
    },
  });
};
